import { isOptionalLayerDisplayFilterValid } from '../displayFilters/displayFilter';
import { effectiveFieldsForJoins } from '../joins/effectiveLayer';
import type { JoinConfig } from '../joins/model';
import { isLayerLabelConfigurationValid } from '../labels/validation';
import { isLayerPopupConfigurationValid } from '../popups/validation';
import type { LayerDefinition } from '../project/model';
import type { AttributeRule, AttributeRuleGroup } from '../rules/model';
import { attributeRuleFieldKind } from '../rules/fields';
import { geometryFamiliesForSource, type GeometryFamily } from '../styles/geometry';
import { isVectorLayerStyleValid } from '../styles/validation';
import type { ImportedVectorData } from './import/model';
import type {
  FieldDefinition,
  FieldValueType,
  SourceDescriptor,
  TableSourceDescriptor,
  VectorSourceDescriptor,
} from './model';

export type ReplacementDependencyArea =
  | 'Categorised styling'
  | 'Display filter'
  | 'Graduated styling'
  | 'Join key'
  | 'Label field'
  | 'Label filter'
  | 'Popup fields'
  | 'Popup title';

export interface ReplacementFieldDependency {
  readonly field: string;
  readonly currentType: FieldValueType;
  readonly areas: readonly ReplacementDependencyArea[];
  readonly joinNames: readonly string[];
  readonly compatibleFields: readonly FieldDefinition[];
  readonly suggestedField: string | null;
  readonly reason: 'incompatible-type' | 'missing';
}

export interface ReplacementTypeChange {
  readonly field: string;
  readonly currentType: FieldValueType;
  readonly replacementType: FieldValueType;
  readonly referenced: boolean;
}

export interface LayerReplacementReview {
  readonly geometry: {
    readonly compatible: boolean;
    readonly currentFamilies: readonly GeometryFamily[];
    readonly replacementFamilies: readonly GeometryFamily[];
  };
  readonly sharedFields: readonly string[];
  readonly addedFields: readonly FieldDefinition[];
  readonly removedFields: readonly FieldDefinition[];
  readonly typeChanges: readonly ReplacementTypeChange[];
  readonly requiredResolutions: readonly ReplacementFieldDependency[];
}

export type ReplacementFieldResolution =
  | { readonly kind: 'map'; readonly replacementField: string }
  | { readonly kind: 'remove-settings' };

export type ReplacementFieldResolutions = Readonly<Record<string, ReplacementFieldResolution>>;

export type PrepareLayerReplacementResult =
  | {
      readonly ok: true;
      readonly source: VectorSourceDescriptor;
      readonly layer: LayerDefinition;
    }
  | { readonly ok: false; readonly message: string };

interface MutableDependency {
  readonly areas: Set<ReplacementDependencyArea>;
  readonly joinNames: Set<string>;
}

function addDependency(
  dependencies: Map<string, MutableDependency>,
  nativeFields: ReadonlySet<string>,
  field: string | null,
  area: ReplacementDependencyArea,
  joinName?: string,
): void {
  if (!field || !nativeFields.has(field)) return;
  const dependency = dependencies.get(field) ?? { areas: new Set(), joinNames: new Set() };
  dependency.areas.add(area);
  if (joinName) dependency.joinNames.add(joinName);
  dependencies.set(field, dependency);
}

function visitRuleFields(rule: AttributeRule | null, visit: (field: string) => void): void {
  if (!rule) return;
  if (rule.kind === 'condition') {
    visit(rule.field);
    return;
  }
  rule.children.forEach((child) => visitRuleFields(child, visit));
}

function dependenciesForLayer(
  layer: LayerDefinition,
  source: VectorSourceDescriptor,
): ReadonlyMap<string, MutableDependency> {
  const nativeFields = new Set(source.fields.map((field) => field.name));
  const dependencies = new Map<string, MutableDependency>();
  const classification = layer.style.classification;
  if (classification.mode === 'categorised') {
    addDependency(dependencies, nativeFields, classification.field, 'Categorised styling');
  } else if (classification.mode === 'graduated') {
    addDependency(dependencies, nativeFields, classification.field, 'Graduated styling');
  }
  addDependency(dependencies, nativeFields, layer.labels.field, 'Label field');
  visitRuleFields(layer.labels.filter, (field) =>
    addDependency(dependencies, nativeFields, field, 'Label filter'),
  );
  addDependency(dependencies, nativeFields, layer.popup.titleField, 'Popup title');
  layer.popup.fields.forEach((field) =>
    addDependency(dependencies, nativeFields, field.field, 'Popup fields'),
  );
  visitRuleFields(layer.displayFilter, (field) =>
    addDependency(dependencies, nativeFields, field, 'Display filter'),
  );
  layer.joins.forEach((join) =>
    addDependency(dependencies, nativeFields, join.leftField, 'Join key', join.name),
  );
  return dependencies;
}

function broadType(type: FieldValueType): string {
  return type === 'integer' || type === 'number' ? 'number' : type;
}

function fieldIsCompatible(
  current: FieldDefinition,
  replacement: FieldDefinition,
  dependency: MutableDependency,
  sources: readonly SourceDescriptor[],
  layer: LayerDefinition,
): boolean {
  const replacementRuleKind = attributeRuleFieldKind(replacement);
  const currentRuleKind = attributeRuleFieldKind(current);
  if (
    (dependency.areas.has('Label field') ||
      dependency.areas.has('Popup fields') ||
      dependency.areas.has('Popup title')) &&
    replacementRuleKind === null
  ) {
    return false;
  }
  if (
    (dependency.areas.has('Display filter') || dependency.areas.has('Label filter')) &&
    replacementRuleKind !== currentRuleKind
  ) {
    return false;
  }
  if (
    dependency.areas.has('Graduated styling') &&
    replacement.type !== 'integer' &&
    replacement.type !== 'number'
  ) {
    return false;
  }
  if (
    dependency.areas.has('Categorised styling') &&
    (!['boolean', 'integer', 'number', 'string'].includes(replacement.type) ||
      broadType(replacement.type) !== broadType(current.type))
  ) {
    return false;
  }
  if (dependency.areas.has('Join key')) {
    const affectedJoins = layer.joins.filter((join) => join.leftField === current.name);
    if (
      affectedJoins.some((join) => {
        const table = sources.find(
          (source): source is TableSourceDescriptor =>
            source.id === join.tableSourceId && source.kind === 'table',
        );
        const right = table?.fields.find((field) => field.name === join.rightField);
        return !right || broadType(right.type) !== broadType(replacement.type);
      })
    ) {
      return false;
    }
  }
  return true;
}

function normalizedFieldName(value: string): string {
  return value.toLocaleLowerCase().replace(/[^a-z0-9]+/g, '');
}

function simpleFieldStem(value: string): string {
  return normalizedFieldName(value).replace(/(?:19|20)\d{2}$/, '');
}

function suggestedField(
  oldName: string,
  compatibleFields: readonly FieldDefinition[],
): string | null {
  const lower = oldName.toLocaleLowerCase();
  const caseInsensitive = compatibleFields.find(
    (field) => field.name.toLocaleLowerCase() === lower,
  );
  if (caseInsensitive) return caseInsensitive.name;
  const normalized = normalizedFieldName(oldName);
  const simple = compatibleFields.filter((field) => normalizedFieldName(field.name) === normalized);
  if (simple.length === 1) return simple[0]?.name ?? null;
  const stem = simpleFieldStem(oldName);
  const changedYear = compatibleFields.filter(
    (field) => stem.length > 0 && simpleFieldStem(field.name) === stem,
  );
  return changedYear.length === 1 ? (changedYear[0]?.name ?? null) : null;
}

function sameFamilies(
  current: readonly GeometryFamily[],
  replacement: readonly GeometryFamily[],
): boolean {
  return (
    current.length === replacement.length && current.every((family) => replacement.includes(family))
  );
}

function importedSource(
  current: VectorSourceDescriptor,
  imported: ImportedVectorData,
): VectorSourceDescriptor {
  return {
    id: current.id,
    name: imported.suggestedLayerName,
    kind: 'vector',
    crs: imported.crs,
    data: imported.data,
    provenance: imported.provenance,
    featureCount: imported.data.features.length,
    fields: imported.fields,
    geometry: imported.geometry,
    extent: imported.extent,
    warnings: imported.warnings,
  };
}

export function inspectLayerReplacement(
  sources: readonly SourceDescriptor[],
  layer: LayerDefinition,
  current: VectorSourceDescriptor,
  imported: ImportedVectorData,
): LayerReplacementReview {
  const candidate = importedSource(current, imported);
  const currentByName = new Map(current.fields.map((field) => [field.name, field]));
  const candidateByName = new Map(candidate.fields.map((field) => [field.name, field]));
  const dependencies = dependenciesForLayer(layer, current);
  const requiredResolutions: ReplacementFieldDependency[] = [];

  for (const [fieldName, dependency] of dependencies) {
    const oldField = currentByName.get(fieldName);
    if (!oldField) continue;
    const exact = candidateByName.get(fieldName);
    if (exact && fieldIsCompatible(oldField, exact, dependency, sources, layer)) continue;
    const compatibleFields = candidate.fields.filter((field) =>
      fieldIsCompatible(oldField, field, dependency, sources, layer),
    );
    requiredResolutions.push({
      field: fieldName,
      currentType: oldField.type,
      areas: [...dependency.areas],
      joinNames: [...dependency.joinNames],
      compatibleFields,
      suggestedField: suggestedField(fieldName, compatibleFields),
      reason: exact ? 'incompatible-type' : 'missing',
    });
  }

  const dependencyNames = new Set(dependencies.keys());
  const typeChanges = current.fields.flatMap((field) => {
    const replacement = candidateByName.get(field.name);
    return replacement && broadType(replacement.type) !== broadType(field.type)
      ? [
          {
            field: field.name,
            currentType: field.type,
            replacementType: replacement.type,
            referenced: dependencyNames.has(field.name),
          },
        ]
      : [];
  });
  const currentFamilies = geometryFamiliesForSource(current);
  const replacementFamilies = geometryFamiliesForSource(candidate);
  return {
    geometry: {
      compatible: sameFamilies(currentFamilies, replacementFamilies),
      currentFamilies,
      replacementFamilies,
    },
    sharedFields: current.fields
      .filter((field) => candidateByName.has(field.name))
      .map((field) => field.name),
    addedFields: candidate.fields.filter((field) => !currentByName.has(field.name)),
    removedFields: current.fields.filter((field) => !candidateByName.has(field.name)),
    typeChanges,
    requiredResolutions,
  };
}

function rewriteRule(
  rule: AttributeRule | null,
  mappings: ReadonlyMap<string, string>,
): AttributeRule | null {
  if (!rule) return null;
  if (rule.kind === 'condition') {
    const field = mappings.get(rule.field);
    return field ? { ...rule, field } : rule;
  }
  return { ...rule, children: rule.children.map((child) => rewriteRule(child, mappings)!) };
}

function ruleUsesFields(rule: AttributeRule | null, fields: ReadonlySet<string>): boolean {
  if (!rule) return false;
  if (rule.kind === 'condition') return fields.has(rule.field);
  return rule.children.some((child) => ruleUsesFields(child, fields));
}

function rewriteLayerFields(
  layer: LayerDefinition,
  mappings: ReadonlyMap<string, string>,
): LayerDefinition {
  const classification = layer.style.classification;
  const mappedClassification =
    classification.mode === 'single'
      ? classification
      : { ...classification, field: mappings.get(classification.field) ?? classification.field };
  return {
    ...layer,
    style: { ...layer.style, classification: mappedClassification },
    labels: {
      ...layer.labels,
      field: layer.labels.field ? (mappings.get(layer.labels.field) ?? layer.labels.field) : null,
      filter: rewriteRule(layer.labels.filter, mappings) as AttributeRuleGroup | null,
    },
    popup: {
      ...layer.popup,
      titleField: layer.popup.titleField
        ? (mappings.get(layer.popup.titleField) ?? layer.popup.titleField)
        : null,
      fields: layer.popup.fields.map((field) => ({
        ...field,
        field: mappings.get(field.field) ?? field.field,
      })),
    },
    displayFilter: rewriteRule(layer.displayFilter, mappings) as AttributeRuleGroup | null,
    joins: layer.joins.map((join) => ({
      ...join,
      leftField: mappings.get(join.leftField) ?? join.leftField,
    })),
  };
}

function removeLayerFieldSettings(
  layer: LayerDefinition,
  removedFields: ReadonlySet<string>,
): LayerDefinition {
  let fieldsToRemove = new Set(removedFields);
  const removedJoins: JoinConfig[] = [];
  const retainedJoins = layer.joins.filter((join) => {
    if (!fieldsToRemove.has(join.leftField)) return true;
    removedJoins.push(join);
    return false;
  });
  if (removedJoins.length > 0) {
    fieldsToRemove = new Set([
      ...fieldsToRemove,
      ...removedJoins.flatMap((join) => join.fields.map((field) => field.outputFieldId)),
    ]);
  }
  const classification = layer.style.classification;
  const removeClassification =
    classification.mode !== 'single' && fieldsToRemove.has(classification.field);
  const removeLabelField = Boolean(layer.labels.field && fieldsToRemove.has(layer.labels.field));
  const labels = {
    ...layer.labels,
    enabled: removeLabelField ? false : layer.labels.enabled,
    field: removeLabelField ? null : layer.labels.field,
    filter: ruleUsesFields(layer.labels.filter, fieldsToRemove) ? null : layer.labels.filter,
  };
  const popupFields = layer.popup.fields.filter((field) => !fieldsToRemove.has(field.field));
  const titleField =
    layer.popup.titleField && fieldsToRemove.has(layer.popup.titleField)
      ? null
      : layer.popup.titleField;
  return {
    ...layer,
    style: removeClassification
      ? { ...layer.style, classification: { mode: 'single' } }
      : layer.style,
    labels,
    popup: {
      ...layer.popup,
      enabled:
        layer.popup.enabled && (titleField !== null || popupFields.some((field) => field.visible)),
      titleField,
      fields: popupFields,
    },
    displayFilter: ruleUsesFields(layer.displayFilter, fieldsToRemove) ? null : layer.displayFilter,
    joins: retainedJoins,
  };
}

function duplicatePopupFields(layer: LayerDefinition): boolean {
  const fields = layer.popup.fields.map((field) => field.field);
  return new Set(fields).size !== fields.length;
}

export function prepareLayerReplacement(
  sources: readonly SourceDescriptor[],
  layer: LayerDefinition,
  current: VectorSourceDescriptor,
  imported: ImportedVectorData,
  resolutions: ReplacementFieldResolutions,
): PrepareLayerReplacementResult {
  const review = inspectLayerReplacement(sources, layer, current, imported);
  if (!review.geometry.compatible) {
    return {
      ok: false,
      message: 'The replacement geometry must use the same point, line, or polygon families.',
    };
  }
  const mappings = new Map<string, string>();
  const removals = new Set<string>();
  for (const dependency of review.requiredResolutions) {
    const resolution = resolutions[dependency.field];
    if (!resolution) {
      return { ok: false, message: `Resolve the missing field “${dependency.field}”.` };
    }
    if (resolution.kind === 'remove-settings') {
      removals.add(dependency.field);
      continue;
    }
    if (!dependency.compatibleFields.some((field) => field.name === resolution.replacementField)) {
      return {
        ok: false,
        message: `Choose a compatible replacement for “${dependency.field}”.`,
      };
    }
    mappings.set(dependency.field, resolution.replacementField);
  }

  let nextLayer = rewriteLayerFields(layer, mappings);
  nextLayer = removeLayerFieldSettings(nextLayer, removals);
  if (duplicatePopupFields(nextLayer)) {
    return {
      ok: false,
      message: 'Two popup fields would map to the same replacement field. Choose distinct fields.',
    };
  }
  const nextSource = importedSource(current, imported);
  const nextSources = sources.map((source) => (source.id === current.id ? nextSource : source));
  const effectiveFields = effectiveFieldsForJoins(nextSources, nextSource, nextLayer.joins);
  if (
    !isVectorLayerStyleValid(nextLayer.style, effectiveFields) ||
    !isLayerLabelConfigurationValid(nextLayer.labels, effectiveFields) ||
    !isLayerPopupConfigurationValid(nextLayer.popup, effectiveFields) ||
    !isOptionalLayerDisplayFilterValid(nextLayer.displayFilter, effectiveFields)
  ) {
    return {
      ok: false,
      message: 'The selected mappings would leave invalid layer settings.',
    };
  }
  return { ok: true, source: nextSource, layer: nextLayer };
}
